/* Centralized scientific sources catalog for all Nutramilo modules.
 *
 *  Used by ScientificSourcesPopover. Each entry is verified peer-reviewed
 *  and corresponds 1:1 to backend logic / rule-base.
 *
 *  Edit sources here; they propagate to all modules that import them.
 */

export const PLATE_SIMULATOR_SOURCES = [
  {
    key: "atkinson",
    title_bg: "Glycemic Load — Atkinson F.S. et al. (2008/2021)",
    title_en: "Glycemic Load — Atkinson F.S. et al. (2008/2021)",
    citation: "International Tables of Glycemic Index and Glycemic Load Values. Diabetes Care 31(12):2281-2283.",
    role_bg: "Източник за GI/GL стойностите. Cutoffs: GL <11 нисък · 11–19 среден · ≥20 висок (ADA).",
    role_en: "Source for GI/GL values. Cutoffs: GL <11 low · 11-19 medium · ≥20 high (ADA).",
    url: "https://doi.org/10.2337/dc08-1239",
  },
  {
    key: "holt",
    title_bg: "Insulin Index — Holt S.H. et al. (1997)",
    title_en: "Insulin Index — Holt S.H. et al. (1997)",
    citation: "An insulin index of foods. American Journal of Clinical Nutrition 66(5):1264-1276.",
    role_bg: "Хваща \"insulin paradox\" храните (млечни, кисело мляко, говеждо) — нисък GI, но висок инсулинов отговор.",
    role_en: "Captures \"insulin paradox\" foods (dairy, yogurt, beef) — low GI, but high insulin response.",
    url: "https://doi.org/10.1093/ajcn/66.5.1264",
  },
  {
    key: "bao",
    title_bg: "Food Insulin Index — Bao J. et al. (2009)",
    title_en: "Food Insulin Index — Bao J. et al. (2009)",
    citation: "Food insulin index: physiologic basis for predicting insulin demand evoked by composite meals. AJCN 90(4):986-992.",
    role_bg: "Прилага инсулиновия индекс върху смесени ястия — основа за \"meal-level\" insulin load изчислението.",
    role_en: "Extends insulin index to composite meals — basis for meal-level insulin load calculation.",
    url: "https://doi.org/10.3945/ajcn.2009.27720",
  },
  {
    key: "nova",
    title_bg: "NOVA класификация — Monteiro C.A. et al. (2019)",
    title_en: "NOVA classification — Monteiro C.A. et al. (2019)",
    citation: "Ultra-processed foods: what they are and how to identify them. Public Health Nutrition 22(5):936-941.",
    role_bg: "NOVA 4 (ultra-processed) храни → ×1.25 multiplier на DIL impact (cardio-metabolic risk evidence).",
    role_en: "NOVA 4 (ultra-processed) foods → ×1.25 multiplier on DIL impact (cardio-metabolic risk evidence).",
    url: "https://doi.org/10.1017/S1368980018003762",
  },
  {
    key: "atwater",
    title_bg: "Atwater енергийни коефициенти",
    title_en: "Atwater energy factors",
    citation: "Atwater W.O. & Bryant A.P. (1900). The Availability and Fuel Value of Food Materials. USDA. Modern: FAO/INFOODS.",
    role_bg: "4-9-4 kcal/g за протеин-мазнини-въглехидрати. Сървърен `macro_validator.py` хваща невалидни макроси.",
    role_en: "4-9-4 kcal/g for protein-fat-carbs. Server-side `macro_validator.py` catches invalid macro combos.",
    url: "https://www.fao.org/4/y5022e/y5022e04.htm",
  },
  {
    key: "usda",
    title_bg: "USDA FoodData Central",
    title_en: "USDA FoodData Central",
    citation: "U.S. Department of Agriculture, Agricultural Research Service. FoodData Central, 2024.",
    role_bg: "Hранителен състав + кофеин mg per 100g/ml за всяка храна в curated DB.",
    role_en: "Nutrient composition + caffeine mg per 100g/ml for every food in the curated DB.",
    url: "https://fdc.nal.usda.gov/",
  },
  {
    key: "kdigo",
    title_bg: "KDIGO 2020/2024 — бъбречни лимити",
    title_en: "KDIGO 2020/2024 — kidney limits",
    // pragma: compliance-allow — peer-reviewed citation, not a patient claim
    citation: "KDIGO 2020 Clinical Practice Guideline on Diabetes Management in CKD; KDIGO 2024 CKD Guideline. Kidney International.",
    role_bg: "K/P/Na/Mg/Ca лимити при бъбречни състояния G3-G5 / диализа — активира се при eGFR<30 или \"on dialysis\" toggle.",
    role_en: "K/P/Na/Mg/Ca limits in advanced kidney conditions G3-G5 / dialysis — activated at eGFR<30 or \"on dialysis\" toggle.",
    url: "https://kdigo.org/guidelines/",
  },
];

export const DIGITAL_TWIN_SOURCES = [
  {
    key: "estruch",
    title_bg: "PREDIMED — Estruch R. et al. (NEJM 2018)",
    title_en: "PREDIMED — Estruch R. et al. (NEJM 2018)",
    citation: "Primary Prevention of Cardiovascular Disease with a Mediterranean Diet. New England Journal of Medicine 378(25):e34.",
    role_bg: "Mediterranean diet → HOMA-IR ↓ ~0.6. Захранва Med-diet branch на Twin симулацията.",
    role_en: "Mediterranean diet → HOMA-IR drop ~0.6. Powers Med-diet branch of Twin simulation.",
    url: "https://doi.org/10.1056/NEJMoa1800389",
  },
  {
    key: "esposito",
    title_bg: "Med-diet & insulin — Esposito K. et al. (JAMA 2004)",
    title_en: "Med-diet & insulin — Esposito K. et al. (JAMA 2004)",
    citation: "Effect of a Mediterranean-Style Diet on Endothelial Dysfunction and Markers of Vascular Inflammation. JAMA 292(12):1440-1446.",
    role_bg: "MedDiet → fasting insulin ↓ ~20%. Calibration на инсулинова резистентност в Twin engine.",
    role_en: "MedDiet → fasting insulin drop ~20%. Insulin resistance calibration in Twin engine.",
    url: "https://doi.org/10.1001/jama.292.12.1440",
  },
  {
    key: "hu_glycemic",
    title_bg: "Glycemic Load & insulin — Hu F.B. et al. (NEJM 2001)",
    title_en: "Glycemic Load & insulin — Hu F.B. et al. (NEJM 2001)",
    citation: "Diet, Lifestyle, and the Risk of Type 2 Diabetes Mellitus in Women. NEJM 345(11):790-797.",
    role_bg: "Low glycemic load → insulin demand ↓ ~25%. Дефинира DIL/Twin coupling effect size.",
    role_en: "Low glycemic load → insulin demand ↓ ~25%. Defines DIL/Twin coupling effect size.",
    url: "https://doi.org/10.1056/NEJMoa010492",
  },
  {
    key: "ros_circulation",
    title_bg: "Nuts/olive oil & inflammation — Ros E. et al. (Circulation 2014)",
    title_en: "Nuts/olive oil & inflammation — Ros E. et al. (Circulation 2014)",
    citation: "Mediterranean Diet and Cardiovascular Health: Teachings of the PREDIMED Study. Adv Nutr 5(3):330S-336S.",
    role_bg: "Ядки/маслиново масло → CRP ↓ ~15%. Захранва anti-inflammatory branch на Twin.",
    role_en: "Nuts/olive oil → CRP drop ~15%. Powers anti-inflammatory branch of Twin.",
    url: "https://doi.org/10.3945/an.113.005389",
  },
  {
    key: "metwally",
    title_bg: "Metabolic Subphenotypes — Metwally A.A. et al. (2024)",
    title_en: "Metabolic Subphenotypes — Metwally A.A. et al. (2024)",
    citation: "Stanford Metabolic Subphenotype Predictor. medRxiv 2024 (aametwally/Metabolic_Subphenotype_Predictor).",
    role_bg: "Subphenotype classifier (insulin resistance, beta-cell, hepatic, muscle) — Twin персонализира интервенциите.",
    role_en: "Subphenotype classifier (IR, β-cell, hepatic, muscle) — Twin personalizes interventions accordingly.",
    url: "https://github.com/aametwally/Metabolic_Subphenotype_Predictor",
  },
  {
    key: "abdul_ghani",
    title_bg: "OGTT subphenotyping — Abdul-Ghani M.A. et al.",
    title_en: "OGTT subphenotyping — Abdul-Ghani M.A. et al.",
    citation: "Insulin Sensitivity and Beta-Cell Function from OGTT Data. Diabetes Care 30(1):89-94.",
    role_bg: "OGTT-derived ISI / Matsuda formulae — backend интерпретация на oral glucose tolerance тестове.",
    role_en: "OGTT-derived ISI / Matsuda formulae — backend interpretation of oral glucose tolerance tests.",
    url: "https://doi.org/10.2337/dc06-1519",
  },
];

export const BIOLOGICAL_AGE_SOURCES = [
  {
    key: "levine_phenoage",
    title_bg: "Levine PhenoAge — Levine M.E. et al. (2018)",
    title_en: "Levine PhenoAge — Levine M.E. et al. (2018)",
    citation: "An epigenetic biomarker of aging for lifespan and healthspan. J Gerontol A Biol Sci Med Sci 73(7):939-945.",
    role_bg: "Основната formula за биологичната възраст в Nutramilo. 9 рутинни маркера → biological age.",
    role_en: "Main biological age formula in Nutramilo. 9 routine markers → biological age estimate.",
    url: "https://doi.org/10.18632/aging.101414",
  },
  {
    key: "belsky_dunedin",
    title_bg: "DunedinPACE — Belsky D.W. et al. (eLife 2022)",
    title_en: "DunedinPACE — Belsky D.W. et al. (eLife 2022)",
    citation: "DunedinPACE, a DNA methylation biomarker of the pace of aging. eLife 11:e73420.",
    role_bg: "Pace-of-aging ratio (биологична / хронологична). Approx-ва се от рутинни labs за достъпност.",
    role_en: "Pace-of-aging ratio (biological / chronological). Approximated from routine labs for accessibility.",
    url: "https://doi.org/10.7554/eLife.73420",
  },
  {
    key: "klemera_doubal",
    title_bg: "Klemera-Doubal Method (KDM) — 2006",
    title_en: "Klemera-Doubal Method (KDM) — 2006",
    citation: "Klemera P. & Doubal S. (2006). A new approach to the concept and computation of biological age. Mech Ageing Dev 127(3):240-248.",
    role_bg: "Класическият KDM модел като cross-check на PhenoAge — повишава confidence interval.",
    role_en: "Classic KDM as cross-check on PhenoAge — increases confidence interval.",
    url: "https://doi.org/10.1016/j.mad.2005.10.004",
  },
];

export const LAB_UPLOAD_SOURCES = [
  {
    key: "kdigo_ckd",
    title_bg: "KDIGO 2024 CKD Guideline",
    title_en: "KDIGO 2024 CKD Guideline",
    // pragma: compliance-allow
    citation: "KDIGO 2024 Clinical Practice Guideline for the Evaluation and Management of CKD. Kidney International.",
    role_bg: "Reference ranges за eGFR, креатинин, urea, ACR — категоризации G1-G5 / A1-A3.",
    role_en: "Reference ranges for eGFR, creatinine, urea, ACR — staging G1-G5 / A1-A3.",
    url: "https://kdigo.org/guidelines/ckd-evaluation-and-management/",
  },
  {
    key: "ata_thyroid",
    title_bg: "ATA Thyroid Guidelines (2014)",
    title_en: "ATA Thyroid Guidelines (2014)",
    citation: "Garber J.R. et al. (2012). Clinical Practice Guidelines for Hypothyroidism. Endocr Pract 18(6):988-1028.",
    role_bg: "TSH, FT4, FT3 reference ranges + age-adjusted upper bounds (по-висок TSH при възрастни).",
    role_en: "TSH, FT4, FT3 reference ranges + age-adjusted upper bounds (higher TSH in elderly).",
    url: "https://doi.org/10.4158/EP12280.GL",
  },
  {
    key: "aha_lipids",
    title_bg: "AHA/ACC Cholesterol Guideline (2018)",
    title_en: "AHA/ACC Cholesterol Guideline (2018)",
    citation: "Grundy S.M. et al. (2019). 2018 AHA/ACC Guideline on the Management of Blood Cholesterol. Circulation 139(25):e1082-e1143.",
    role_bg: "LDL/HDL/триглицериди cutoffs, ASCVD risk staging, statin eligibility hints.",
    role_en: "LDL/HDL/triglyceride cutoffs, ASCVD risk staging, statin eligibility hints.",
    url: "https://doi.org/10.1161/CIR.0000000000000625",
  },
  {
    key: "ada_glycemic",
    title_bg: "ADA Standards of Care (2024)",
    title_en: "ADA Standards of Care (2024)",
    citation: "American Diabetes Association. Standards of Care in Diabetes—2024. Diabetes Care 47(Suppl 1).",
    role_bg: "HbA1c, FPG, OGTT cutoffs (нормални / преддиабетни / диабетни класификации). Не е диагностика — само reference ranges.",
    role_en: "HbA1c, FPG, OGTT cutoffs (normal / pre-diabetes / diabetes ranges). Not diagnostic — only reference ranges.",
    url: "https://diabetesjournals.org/care/issue/47/Supplement_1",
  },
  {
    key: "esh_bp",
    title_bg: "ESH/ESC Hypertension Guideline (2023)",
    title_en: "ESH/ESC Hypertension Guideline (2023)",
    citation: "Mancia G. et al. (2023). 2023 ESH Guidelines for the management of arterial hypertension. J Hypertens 41(12):1874-2071.",
    role_bg: "BP staging (optimal / normal / high-normal / grade 1-3). Захранва lab interpretation engine.",
    role_en: "BP staging (optimal / normal / high-normal / grade 1-3). Powers lab interpretation engine.",
    url: "https://doi.org/10.1097/HJH.0000000000003480",
  },
  {
    key: "iom_vitamins",
    title_bg: "IOM Dietary Reference Intakes",
    title_en: "IOM Dietary Reference Intakes",
    citation: "Institute of Medicine. Dietary Reference Intakes for Vitamins and Minerals (1997-2011 series). National Academies Press.",
    role_bg: "Vitamin D, B12, Ferritin, Folate, Iron — reference ranges и deficiency thresholds.",
    role_en: "Vitamin D, B12, Ferritin, Folate, Iron — reference ranges and deficiency thresholds.",
    url: "https://www.nap.edu/topic/278/dietary-reference-intakes",
  },
];
